/**
 * =============================================================================
 * IDS CRM Web - Farm Site Create/Edit Form
 * =============================================================================
 *
 * PURPOSE:
 * Form for operational farm sites beneath a corporate end customer.
 *
 * =============================================================================
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import CountrySelect from '../components/CountrySelect';
import { resizeMilkingTimes, milkingSchedulePayload } from '../utils/milkingTimes';
import { yieldUnitLabel, litresToDisplayYield, displayYieldToLitres } from '../utils/milkYield';
import UseMyLocationButton from '../components/UseMyLocationButton';
import { deviceGeocodeFields } from '../utils/deviceLocationPin';
import { validateCoordinatePair } from '../utils/coordinates';
import { SITE_TYPES, SITE_TYPE_KEYS, DEFAULT_SITE_TYPE, ALL_TYPE_FIELDS, normaliseSiteType, recordsField, recordsAnyField } from '../utils/siteTypes';
import { CUSTOMER_STATUS_ORDER, CUSTOMER_STATUS_META } from '../utils/customerStatus';
import './CustomerForm.css';

const createEmptySiteData = (customer = {}) => ({
  site_name: '',
  site_code: '',
  is_primary: false,
  // A new site starts with its customer's status (Gareth 2026-09-14).
  status: CUSTOMER_STATUS_ORDER.includes(customer.status) ? customer.status : 'active',
  site_type: DEFAULT_SITE_TYPE,
  address_line_1: '',
  address_line_2: '',
  town_city: '',
  county_region: '',
  state_province: '',
  postcode: '',
  country_code: customer.country_code || 'GB',
  timezone: '',
  lat: '',
  lng: '',
  geocode_source: '',
  geocode_status: '',
  geocode_last_checked: '',
  geocode_accuracy: '',
  travel_time_override_mins: '',
  parlour_type: '',
  parlour_manufacturer: '',
  parlour_manufacturer_other: '',
  parlour_size: '',
  rotation_direction: '',
  milkings_per_day: '',
  milking_start_times: [],
  milking_end_times: [],
  parlour_speed_secs_per_stall: '',
  cows_onsite: '',
  cows_milking: '',
  cattle_onsite: '',
  breed: '',
  average_yield_litres: '',
  milk_buyer: '',
  milk_contract_type: '',
  bedding_type: '',
  chemical_type: '',
  notes: '',
  servicing_depot_id: ''
});

/**
 * Arriving from customer creation with "site address same as business address"
 * ticked: the site starts with the customer's address AND its already-geocoded
 * coordinates — customer addresses geocode server-side on create, so asking
 * the engineer to Find Coordinates again for the same address is a wasted
 * lookup. A first site created this way is the primary by definition.
 */
const prefilledFromCustomer = (customer) => ({
  ...createEmptySiteData(customer),
  is_primary: true,
  site_name: customer.business_name ? `${customer.business_name} - Main Site` : '',
  address_line_1: customer.address_line_1 || '',
  address_line_2: customer.address_line_2 || '',
  town_city: customer.town_city || '',
  county_region: customer.county_region || '',
  state_province: customer.state_province || '',
  postcode: customer.postcode || '',
  country_code: customer.country_code || 'GB',
  timezone: customer.timezone || '',
  lat: customer.lat != null ? String(customer.lat) : '',
  lng: customer.lng != null ? String(customer.lng) : '',
  geocode_source: customer.lat != null ? (customer.geocode_source || 'nominatim') : '',
  geocode_status: customer.lat != null ? 'geocoded' : '',
  geocode_last_checked: customer.lat != null ? (customer.geocoded_at || '') : ''
});

function FarmSiteForm() {
  const { guardElement, markDirty } = useUnsavedChanges();
  const { id, siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const isEdit = Boolean(siteId);

  // Handed over from customer creation (see CustomerForm's forced popup).
  const prefillAddress = Boolean(location.state?.prefillAddress);
  // Mid-flow creations (e.g. New Opportunity) come back to their caller after
  // the site saves. Internal app paths only, as on the customer form.
  const rawReturnTo = searchParams.get('return_to');
  const returnTo = rawReturnTo && rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//')
    ? rawReturnTo
    : null;

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(createEmptySiteData());
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState({ status: '', message: '', addressKey: '' });
  // Depots of the customer's distributor chain (master + sub), grouped by
  // distributor when both have depots. Empty -> the picker stays hidden.
  const [depotGroups, setDepotGroups] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const customerRes = await authFetch(`/api/v1/customers/${id}`);
        if (!customerRes.ok) throw new Error('Customer not found');
        const customerData = await customerRes.json();
        setCustomer(customerData);

        if (!isEdit) {
          setFormData(prefillAddress ? prefilledFromCustomer(customerData) : createEmptySiteData(customerData));
          return;
        }

        const siteRes = await authFetch(`/api/v1/customers/${id}/sites/${siteId}`);
        if (!siteRes.ok) throw new Error('Farm site not found');
        const site = await siteRes.json();
        setFormData({
          site_name: site.site_name || '',
          site_code: site.site_code || '',
          is_primary: Boolean(site.is_primary),
          status: site.status || 'active',
          site_type: normaliseSiteType(site.site_type),
          address_line_1: site.address_line_1 || '',
          address_line_2: site.address_line_2 || '',
          town_city: site.town_city || '',
          county_region: site.county_region || '',
          state_province: site.state_province || '',
          postcode: site.postcode || '',
          country_code: site.country_code || customerData.country_code || 'GB',
          timezone: site.timezone || '',
          lat: site.lat || '',
          lng: site.lng || '',
          geocode_source: site.geocode_source || '',
          geocode_status: site.geocode_status || '',
          geocode_last_checked: site.geocode_last_checked || '',
          geocode_accuracy: site.geocode_accuracy || '',
          travel_time_override_mins: site.travel_time_override_mins || '',
          parlour_type: site.parlour_type || '',
          parlour_manufacturer: site.parlour_manufacturer || '',
          parlour_manufacturer_other: site.parlour_manufacturer_other || '',
          parlour_size: site.parlour_size || '',
          rotation_direction: site.rotation_direction || '',
          milkings_per_day: site.milkings_per_day || '',
          milking_start_times: Array.isArray(site.milking_start_times) ? site.milking_start_times : [],
          milking_end_times: Array.isArray(site.milking_end_times) ? site.milking_end_times : [],
          parlour_speed_secs_per_stall: site.parlour_speed_secs_per_stall || '',
          cows_onsite: site.cows_onsite || '',
          cows_milking: site.cows_milking || '',
          cattle_onsite: site.cattle_onsite || '',
          breed: site.breed || '',
          // Stored litres -> lbs for US sites; the form works in display units.
          average_yield_litres: litresToDisplayYield(
            site.average_yield_litres,
            site.country_code || customerData.country_code
          ),
          milk_buyer: site.milk_buyer || '',
          milk_contract_type: site.milk_contract_type || '',
          bedding_type: site.bedding_type || '',
          chemical_type: site.chemical_type || '',
          notes: site.notes || '',
          servicing_depot_id: site.servicing_depot_id || ''
        });
      } catch (err) {
        setError(err.message || 'Failed to load farm site');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authFetch, id, isEdit, siteId, prefillAddress]);

  // Load the depots of the customer's distributor chain for the
  // "Serviced by depot" picker (informational link, one depot per site).
  useEffect(() => {
    if (!customer) return;
    const distributorIds = [...new Set(
      [customer.master_distributor_id, customer.sub_distributor_id].filter(Boolean)
    )];
    if (distributorIds.length === 0) {
      setDepotGroups([]);
      return;
    }

    let cancelled = false;
    const loadDepots = async () => {
      try {
        const groups = await Promise.all(distributorIds.map(async (distributorId) => {
          const response = await authFetch(`/api/v1/distributors/${distributorId}/depots`);
          if (!response.ok) return null;
          const body = await response.json();
          const depots = body.data || [];
          if (depots.length === 0) return null;
          return {
            distributorId,
            label: distributorId === customer.master_distributor_id
              ? (customer.master_distributor_name || 'Distributor')
              : (customer.sub_distributor_name || 'Sub-distributor'),
            depots
          };
        }));
        if (!cancelled) setDepotGroups(groups.filter(Boolean));
      } catch (err) {
        // Non-fatal: the picker simply stays hidden.
      }
    };
    loadDepots();
    return () => { cancelled = true; };
  }, [authFetch, customer]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (['address_line_1', 'address_line_2', 'town_city', 'county_region', 'state_province', 'postcode', 'country_code'].includes(name)) {
      setGeocodeResult({ status: '', message: '', addressKey: '' });
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(['lat', 'lng'].includes(name) ? {
        geocode_status: 'manual',
        geocode_source: 'manual',
        geocode_accuracy: ''
      } : {})
    }));
  };

  // Never truncates: typed times survive the count field being cleared or
  // lowered while editing. The count decides what renders and what saves
  // (milkingSchedulePayload), not what state remembers.
  const setMilkingTime = (field, index, value) => {
    setFormData(prev => {
      const times = Array.isArray(prev[field]) ? [...prev[field]] : [];
      while (times.length <= index) times.push('');
      times[index] = value;
      return { ...prev, [field]: times };
    });
  };

  // Shared with the depot and accommodation forms so the three cannot drift.
  const validateCoordinates = (lat, lng) =>
    validateCoordinatePair(lat, lng, { noun: 'customer sites' });

  const parseGeocodeCoordinates = (data) => {
    const parsedLat = Number(data.latitude);
    const parsedLng = Number(data.longitude);
    if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
      throw new Error('No valid coordinates were found for this address.');
    }
    const coordinateError = validateCoordinates(parsedLat, parsedLng);
    if (coordinateError) {
      throw new Error(coordinateError);
    }
    return { lat: parsedLat, lng: parsedLng };
  };

  const buildAddressPayload = () => ({
    addressLine1: formData.address_line_1,
    addressLine2: formData.address_line_2,
    city: formData.town_city,
    countyState: formData.county_region,
    state: formData.state_province,
    postcodeZip: formData.postcode,
    country: formData.country_code
  });

  const buildAddressKey = (address) => [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.countyState,
    address.state,
    address.postcodeZip,
    address.country
  ].map(value => (value || '').trim().toLowerCase()).join('|');

  const findCoordinates = async () => {
    const address = buildAddressPayload();
    const addressKey = buildAddressKey(address);
    const hasSearchAddress = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.countyState,
      address.postcodeZip
    ].some(value => (value || '').trim());

    if (!hasSearchAddress) {
      setGeocodeResult({ status: 'error', message: 'Enter a site address before finding coordinates.', addressKey: '' });
      return;
    }

    if (geocodeResult.addressKey === addressKey && formData.lat && formData.lng) {
      setGeocodeResult(prev => ({
        ...prev,
        status: 'success',
        message: prev.message || 'Coordinates already found for this unchanged address. Save the site to confirm them.'
      }));
      return;
    }

    setGeocoding(true);
    setGeocodeResult({ status: '', message: '', addressKey: '' });

    try {
      const response = await authFetch('/api/v1/geocode-address', {
        method: 'POST',
        body: JSON.stringify({
          ...address,
          customer_id: id,
          site_id: siteId || undefined
        })
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'No matching address found');
      }

      const coordinates = parseGeocodeCoordinates(data);
      const checkedAt = new Date().toISOString();
      setFormData(prev => ({
        ...prev,
        lat: String(coordinates.lat),
        lng: String(coordinates.lng),
        geocode_source: data.source || 'nominatim',
        geocode_status: 'geocoded',
        geocode_last_checked: checkedAt,
        geocode_accuracy: data.raw?.type || data.raw?.class || ''
      }));
      setGeocodeResult({
        status: 'success',
        message: `Matched: ${data.displayName || 'address found'}. Save the site to confirm these coordinates.`,
        addressKey
      });
    } catch (err) {
      setGeocodeResult({
        status: 'error',
        message: err.message || 'No matching address found',
        addressKey: ''
      });
    } finally {
      setGeocoding(false);
    }
  };

  /**
   * Store a device pin. The shared helper decides the wording and whether the
   * fix is good enough to confirm; this only has to decide what to write.
   */
  const handleDevicePin = (result) => {
    if (!result.ok) {
      setGeocodeResult({ status: result.status, message: result.message, addressKey: '' });
      return;
    }

    const coordinateError = validateCoordinates(result.lat, result.lng);
    if (coordinateError) {
      setGeocodeResult({ status: 'error', message: coordinateError, addressKey: '' });
      return;
    }

    setFormData(prev => ({
      ...prev,
      lat: result.lat,
      lng: result.lng,
      ...deviceGeocodeFields(result),
      geocode_last_checked: new Date().toISOString()
    }));
    setGeocodeResult({ status: result.status, message: result.message, addressKey: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitAction = e.nativeEvent.submitter?.value || 'close';
    setSaving(true);
    setError(null);

    const coordinateError = validateCoordinates(formData.lat, formData.lng);
    if (coordinateError) {
      setError(coordinateError);
      setSaving(false);
      return;
    }

    // A site still on a retired status (e.g. At Risk) must pick one of the
    // three, as on the customer form.
    if (!CUSTOMER_STATUS_ORDER.includes(formData.status)) {
      setError('Please complete the required field: Status');
      setSaving(false);
      return;
    }

    const numericFields = [
      'lat', 'lng', 'travel_time_override_mins', 'parlour_size',
      'milkings_per_day', 'parlour_speed_secs_per_stall',
      'cows_onsite', 'cows_milking', 'average_yield_litres'
    ];
    const payload = { ...formData };

    Object.keys(payload).forEach(key => {
      if (payload[key] === '') payload[key] = null;
    });

    numericFields.forEach(field => {
      if (payload[field] !== null && payload[field] !== undefined) {
        payload[field] = Number(payload[field]);
      }
    });

    // ⚠️ Both of these used to run unconditionally, and that was a data-loss
    // hazard the moment site types hid the milking fields: milkingSchedulePayload
    // returns an explicit { milking_start_times: null, milking_end_times: null }
    // when there are no times, so saving a Beef site would have NULLed a dairy
    // schedule that is meant to be hidden, not erased. Only send a field the
    // site's type actually records. (The API drops them too — see
    // normaliseSiteType in routes/customers.js — but the form must not send
    // destructive values in the first place.)
    if (recordsField(formData.site_type, 'average_yield_litres')) {
      // The form works in display units (lbs for US sites) — store litres.
      payload.average_yield_litres = displayYieldToLitres(formData.average_yield_litres, formData.country_code);
    } else {
      delete payload.average_yield_litres;
    }

    if (recordsField(formData.site_type, 'milking_start_times')) {
      Object.assign(payload, milkingSchedulePayload(formData));
    } else {
      delete payload.milking_start_times;
      delete payload.milking_end_times;
    }

    // Every other field this type does not record is dropped for the same
    // reason: hidden data stays exactly as it was stored.
    ALL_TYPE_FIELDS
      .filter(field => !recordsField(formData.site_type, field))
      .forEach(field => { delete payload[field]; });

    try {
      const response = await authFetch(
        isEdit ? `/api/v1/customers/${id}/sites/${siteId}` : `/api/v1/customers/${id}/sites`,
        {
          method: isEdit ? 'PUT' : 'POST',
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        if (!isEdit && submitAction === 'addAnother') {
          setFormData(createEmptySiteData(customer));
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        // Mid-flow creation (customer → site → back to e.g. New Opportunity):
        // the caller gets the new customer pre-selected, same contract as the
        // customer form's own return_to round-trip.
        if (returnTo && !isEdit) {
          const separator = returnTo.includes('?') ? '&' : '?';
          navigate(`${returnTo}${separator}customer_id=${id}`);
          return;
        }

        navigate(`/customers/${id}?tab=sites`);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save farm site');
      }
    } catch (err) {
      setError('Failed to save farm site');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-page">Loading farm site...</div>;

  const milkingCount = parseInt(formData.milkings_per_day, 10) || 0;
  const milkingStartTimes = resizeMilkingTimes(formData.milking_start_times, milkingCount);
  const milkingEndTimes = resizeMilkingTimes(formData.milking_end_times, milkingCount);

  return (
    <div className="customer-form-page">
      <div className="form-header">
        <Link to={`/customers/${id}`} className="back-link">
          ← {customer?.business_name || "Customer"}
        </Link>
        <h1>{isEdit ? 'Edit Farm Site' : 'New Farm Site'}</h1>
      </div>

      {guardElement}
      <form onChangeCapture={markDirty} onSubmit={handleSubmit} className="customer-form">
        {error && <div className="form-error">{error}</div>}

        <div className="form-section">
          <h2>Site Identity</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="site_name">Site Name *</label>
              <input id="site_name" name="site_name" value={formData.site_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="site_code">Site Code</label>
              <input id="site_code" name="site_code" value={formData.site_code} onChange={handleChange} placeholder="e.g., DN-01" />
            </div>
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select id="status" name="status" value={formData.status} onChange={handleChange}>
                {/* A site still on a retired status shows it needs choosing. */}
                {!CUSTOMER_STATUS_ORDER.includes(formData.status) && (
                  <option value={formData.status} disabled>Choose a status</option>
                )}
                {CUSTOMER_STATUS_ORDER.map(status => (
                  <option key={status} value={status}>{CUSTOMER_STATUS_META[status].label}</option>
                ))}
              </select>
            </div>
            {/* Decides which sections below are collected. Changing it hides
                the other type's figures, it never deletes them. */}
            <div className="form-group">
              <label htmlFor="site_type">Site Type</label>
              <select id="site_type" name="site_type" value={formData.site_type} onChange={handleChange}>
                {SITE_TYPE_KEYS.map(key => (
                  <option key={key} value={key}>{SITE_TYPES[key].label}</option>
                ))}
              </select>
              <span className="field-help">{SITE_TYPES[normaliseSiteType(formData.site_type)].hint}</span>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input type="checkbox" name="is_primary" checked={formData.is_primary} onChange={handleChange} />
                Primary site
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Site Address</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="address_line_1">Address Line 1</label>
              <input id="address_line_1" name="address_line_1" value={formData.address_line_1} onChange={handleChange} />
            </div>
            <div className="form-group full-width">
              <label htmlFor="address_line_2">Address Line 2</label>
              <input id="address_line_2" name="address_line_2" value={formData.address_line_2} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="town_city">Town/City</label>
              <input id="town_city" name="town_city" value={formData.town_city} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="county_region">County/Region</label>
              <input id="county_region" name="county_region" value={formData.county_region} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="state_province">State/Province</label>
              <input id="state_province" name="state_province" value={formData.state_province} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="postcode">Postcode/Zipcode</label>
              <input id="postcode" name="postcode" value={formData.postcode} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="country_code">Country</label>
              <CountrySelect id="country_code" name="country_code" value={formData.country_code} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="lat">Latitude</label>
              <input id="lat" name="lat" type="number" step="0.0000001" value={formData.lat} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="lng">Longitude</label>
              <input id="lng" name="lng" type="number" step="0.0000001" value={formData.lng} onChange={handleChange} />
            </div>
            {depotGroups.length > 0 && (
              <div className="form-group">
                <label htmlFor="servicing_depot_id">Serviced by depot</label>
                <select
                  id="servicing_depot_id"
                  name="servicing_depot_id"
                  value={formData.servicing_depot_id}
                  onChange={handleChange}
                >
                  <option value="">Not assigned</option>
                  {depotGroups.map(group => (
                    <optgroup key={group.distributorId} label={group.label}>
                      {group.depots.map(depot => (
                        <option key={depot.depot_id} value={depot.depot_id}>
                          {depot.depot_name}{depot.is_head_office ? ' (Head office)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group full-width geocode-panel">
              <div className="geocode-actions">
                <button
                  type="button"
                  className="btn-geocode"
                  onClick={findCoordinates}
                  disabled={geocoding}
                >
                  {geocoding ? 'Finding...' : 'Find coordinates'}
                </button>
                <UseMyLocationButton noun="site" disabled={geocoding} onResult={handleDevicePin} />
                <span className="field-help">
                  Find coordinates looks up the address, which on farms often lands nearby rather than
                  on the buildings. If you are standing at the site, use your current location instead —
                  it pins the exact spot and is kept as a manual fix. Save the site to confirm.
                </span>
              </div>
              {geocodeResult.message && (
                <div className={`geocode-message ${geocodeResult.status}`}>
                  {geocodeResult.message}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Farm Details</h2>
          <div className="form-grid">
            {recordsField(formData.site_type, 'cows_onsite') && (
              <>
                <div className="form-group">
                  <label htmlFor="cows_onsite">Total Herd Size</label>
                  <input id="cows_onsite" name="cows_onsite" type="number" min="0" value={formData.cows_onsite} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="cows_milking">Cows Milking</label>
                  <input id="cows_milking" name="cows_milking" type="number" min="0" value={formData.cows_milking} onChange={handleChange} />
                </div>
              </>
            )}
            {recordsField(formData.site_type, 'cattle_onsite') && (
              <div className="form-group">
                <label htmlFor="cattle_onsite">Cattle on Site</label>
                <input id="cattle_onsite" name="cattle_onsite" type="number" min="0" value={formData.cattle_onsite} onChange={handleChange} />
                <span className="field-help">Counted separately from milking cows in herd reporting.</span>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="breed">Breed</label>
              <input id="breed" name="breed" value={formData.breed} onChange={handleChange} />
            </div>
            {recordsField(formData.site_type, 'average_yield_litres') && (
              <>
                <div className="form-group">
                  <label htmlFor="average_yield_litres">Average Yield ({yieldUnitLabel(formData.country_code)})</label>
                  <input id="average_yield_litres" name="average_yield_litres" type="number" min="0" step="0.1" value={formData.average_yield_litres} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="milk_buyer">Milk Buyer</label>
                  <input id="milk_buyer" name="milk_buyer" value={formData.milk_buyer} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="milk_contract_type">Milk Contract</label>
                  <select id="milk_contract_type" name="milk_contract_type" value={formData.milk_contract_type} onChange={handleChange}>
                    <option value="">Select...</option>
                    <option value="standard">Standard</option>
                    <option value="organic">Organic</option>
                    <option value="high_welfare">High Welfare</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="bedding_type">Bedding Type</label>
              <input id="bedding_type" name="bedding_type" value={formData.bedding_type} onChange={handleChange} placeholder="e.g., Sand, Sawdust, Mattresses" />
            </div>
            <div className="form-group">
              <label htmlFor="chemical_type">Chemical Type</label>
              <input id="chemical_type" name="chemical_type" value={formData.chemical_type} onChange={handleChange} placeholder="e.g., Peracetic acid, Chlorine dioxide" />
            </div>
            {recordsField(formData.site_type, 'parlour_type') && (
              <>
              <div className="form-group">
                <label htmlFor="parlour_type">Parlour Type</label>
                <select id="parlour_type" name="parlour_type" value={formData.parlour_type} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="rotary">Rotary</option>
                  <option value="herringbone">Herringbone</option>
                  <option value="parallel">Parallel</option>
                  <option value="swingover">Swingover</option>
                  <option value="robotic">Robotic</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="parlour_manufacturer">Parlour Manufacturer</label>
                <select id="parlour_manufacturer" name="parlour_manufacturer" value={formData.parlour_manufacturer} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="DeLaval">DeLaval</option>
                  <option value="GEA">GEA</option>
                  <option value="Waikato">Waikato</option>
                  <option value="BouMatic">BouMatic</option>
                  <option value="SAC">SAC</option>
                  <option value="Fullwood">Fullwood</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {formData.parlour_manufacturer === 'other' && (
                <div className="form-group">
                  <label htmlFor="parlour_manufacturer_other">Manufacturer Name</label>
                  <input id="parlour_manufacturer_other" name="parlour_manufacturer_other" value={formData.parlour_manufacturer_other} onChange={handleChange} />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="parlour_size">Parlour Size</label>
                <input id="parlour_size" name="parlour_size" type="number" min="0" value={formData.parlour_size} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="rotation_direction">Rotation Direction</label>
                <select id="rotation_direction" name="rotation_direction" value={formData.rotation_direction} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="clockwise">Clockwise</option>
                  <option value="anticlockwise">Anticlockwise</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="parlour_speed_secs_per_stall">Parlour Speed (secs per stall)</label>
                <input id="parlour_speed_secs_per_stall" name="parlour_speed_secs_per_stall" type="number" min="0.1" step="0.1" placeholder="e.g. 4.5" value={formData.parlour_speed_secs_per_stall} onChange={handleChange} />
              </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="travel_time_override_mins">Travel Time Override (mins)</label>
              <input id="travel_time_override_mins" name="travel_time_override_mins" type="number" min="0" value={formData.travel_time_override_mins} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Beef sites have no parlour, so the whole section goes. Hidden, not
            erased: a dairy site switched to Beef keeps its stored schedule. */}
        {recordsAnyField(formData.site_type, ['milkings_per_day', 'milking_start_times']) && (
          <div className="form-section">
            <h2>Milking Schedule</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="milkings_per_day">Milkings per Day</label>
                <input id="milkings_per_day" name="milkings_per_day" type="number" min="1" max="4" value={formData.milkings_per_day} onChange={handleChange} />
              </div>
              {milkingCount === 0 && (
                <div className="form-group full-width">
                  <span className="field-help">Enter the milkings per day to record start and end times for each milking.</span>
                </div>
              )}
              {milkingStartTimes.map((startTime, index) => (
                <React.Fragment key={`milking-${index}`}>
                  <div className="form-group">
                    <label htmlFor={`milking_start_${index}`}>Milking {index + 1} Start (local time)</label>
                    <input
                      type="time"
                      id={`milking_start_${index}`}
                      value={startTime}
                      onChange={(e) => setMilkingTime('milking_start_times', index, e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`milking_end_${index}`}>Milking {index + 1} End (optional)</label>
                    <input
                      type="time"
                      id={`milking_end_${index}`}
                      value={milkingEndTimes[index]}
                      onChange={(e) => setMilkingTime('milking_end_times', index, e.target.value)}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>Notes</h2>
          <div className="form-group full-width">
            <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} rows={4} />
          </div>
        </div>

        <div className="form-actions">
          <Link to={`/customers/${id}?tab=sites`} className="btn-cancel">Cancel</Link>
          <button type="submit" name="submitAction" value="close" className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Farm Site')}
          </button>
          {!isEdit && (
            <button
              type="submit"
              name="submitAction"
              value="addAnother"
              className="btn-save"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Create & Add Another'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default FarmSiteForm;
